import React, { useEffect, useMemo, useState } from 'react'
import type {
  EmployeeGoldFactMonthRow,
  EmployeeManifestUser,
} from '../../../types/employeeAnalysis'
import type { PeriodFilter } from '../../../utils/projectAnalysisPeriod'
import { isRecordInPeriod } from '../../../utils/projectAnalysisPeriod'

const BATCH = 30

interface AggRow {
  user_id: number
  full_name?: string
  username?: string
  email?: string
  is_active?: number | boolean
  total_hours: number
  billable_hours: number
  non_billable_hours: number
  utilization_rate: number
  late_entries: number
  months_count: number
}

const METRICS = [
  { value: 'billable_hours', label: 'Billable hours' },
  { value: 'utilization_rate', label: 'Utilization rate' },
  { value: 'underutilized', label: 'Underutilized' },
  { value: 'non_billable_hours', label: 'Non-billable hours' },
  { value: 'late_entries', label: 'Late entries' },
  { value: 'total_hours', label: 'Total hours' },
] as const

type Metric = (typeof METRICS)[number]['value']

export interface EmployeeTopNSectionProps {
  periodFilter: PeriodFilter
  statusFilter: 'all' | 'active' | 'inactive'
  onUserSelect: (userId: number) => void
}

export function EmployeeTopNSection({
  periodFilter,
  statusFilter,
  onUserSelect,
}: EmployeeTopNSectionProps) {
  const [topNData, setTopNData] = useState<AggRow[]>([])
  const [loading, setLoading] = useState(true)
  const [topN, setTopN] = useState(5)
  const [metric, setMetric] = useState<Metric>('billable_hours')

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      setLoading(true)
      try {
        const manifestRes = await fetch('/ts_user_jsons/manifest.json')
        if (!manifestRes.ok) throw new Error('manifest')
        const manifest = await manifestRes.json()
        const users: EmployeeManifestUser[] = manifest.users || []
        const allUserData: { user: EmployeeManifestUser; data: EmployeeGoldFactMonthRow[] }[] = []

        for (let i = 0; i < users.length; i += BATCH) {
          if (cancelled) return
          const batch = users.slice(i, i + BATCH)
          const results = await Promise.all(
            batch.map(async (user) => {
              try {
                const response = await fetch(
                  `/ts_user_jsons/gold_fact_user_monthly_with_projects_user_id_${user.user_id}.json`,
                )
                if (!response.ok) return null
                const data: EmployeeGoldFactMonthRow[] = await response.json()
                return { user, data }
              } catch {
                return null
              }
            }),
          )
          allUserData.push(...results.filter(Boolean) as typeof allUserData)
        }

        if (cancelled) return

        const hasPeriod =
          periodFilter &&
          (periodFilter.year !== 'all' || periodFilter.fy !== 'all')

        const aggregated: AggRow[] = allUserData
          .map(({ user, data }) => {
            const filteredData = hasPeriod
              ? data.filter((d) =>
                  isRecordInPeriod(d.year, d.month, periodFilter),
                )
              : data
            if (filteredData.length === 0) return null

            const totalHours = filteredData.reduce(
              (sum, d) => sum + parseFloat(String(d.total_hours || 0)),
              0,
            )
            const billableHours = filteredData.reduce(
              (sum, d) => sum + parseFloat(String(d.billable_hours || 0)),
              0,
            )
            const nonBillableHours = filteredData.reduce(
              (sum, d) => sum + parseFloat(String(d.non_billable_hours || 0)),
              0,
            )
            const lateEntries = filteredData.reduce(
              (sum, d) => sum + parseInt(String(d.late_entries_count || 0), 10),
              0,
            )
            const utilizationRate =
              totalHours > 0 ? (billableHours / totalHours) * 100 : 0

            return {
              user_id: user.user_id,
              full_name: user.full_name,
              username: user.username,
              email: user.email,
              is_active: user.is_active,
              total_hours: totalHours,
              billable_hours: billableHours,
              non_billable_hours: nonBillableHours,
              utilization_rate: utilizationRate,
              late_entries: lateEntries,
              months_count: filteredData.length,
            }
          })
          .filter(Boolean) as AggRow[]

        let finalList = aggregated
        if (statusFilter === 'active')
          finalList = aggregated.filter(
            (u) => u.is_active === 1 || u.is_active === true,
          )
        else if (statusFilter === 'inactive')
          finalList = aggregated.filter(
            (u) => !u.is_active && u.is_active !== 1,
          )

        setTopNData(finalList)
      } catch {
        setTopNData([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [periodFilter, statusFilter])

  const getTopN = useMemo(() => {
    if (!topNData.length) return []
    let sorted = [...topNData]
    switch (metric) {
      case 'billable_hours':
        sorted.sort((a, b) => b.billable_hours - a.billable_hours)
        break
      case 'utilization_rate':
        sorted = sorted.filter((u) => u.total_hours >= 100)
        sorted.sort((a, b) => b.utilization_rate - a.utilization_rate)
        break
      case 'underutilized':
        sorted = sorted.filter((u) => u.total_hours >= 100)
        sorted.sort((a, b) => a.utilization_rate - b.utilization_rate)
        break
      case 'non_billable_hours':
        sorted.sort((a, b) => b.non_billable_hours - a.non_billable_hours)
        break
      case 'late_entries':
        sorted.sort((a, b) => b.late_entries - a.late_entries)
        break
      case 'total_hours':
        sorted.sort((a, b) => b.total_hours - a.total_hours)
        break
      default:
        break
    }
    return sorted.slice(0, topN)
  }, [topNData, metric, topN])

  const displayValue = (user: AggRow) => {
    switch (metric) {
      case 'billable_hours':
        return `${user.billable_hours.toLocaleString(undefined, { maximumFractionDigits: 1 })} hrs`
      case 'utilization_rate':
      case 'underutilized':
        return `${user.utilization_rate.toFixed(1)}%`
      case 'non_billable_hours':
        return `${user.non_billable_hours.toLocaleString(undefined, { maximumFractionDigits: 1 })} hrs`
      case 'late_entries':
        return user.late_entries.toLocaleString()
      case 'total_hours':
        return `${user.total_hours.toLocaleString(undefined, { maximumFractionDigits: 1 })} hrs`
      default:
        return '—'
    }
  }

  const barWidth = (user: AggRow) => {
    if (!getTopN.length) return 0
    const maxUser = getTopN[0]
    switch (metric) {
      case 'billable_hours':
        return maxUser.billable_hours > 0
          ? (user.billable_hours / maxUser.billable_hours) * 100
          : 0
      case 'utilization_rate':
        return user.utilization_rate
      case 'underutilized':
        return 100 - user.utilization_rate
      case 'non_billable_hours':
        return maxUser.non_billable_hours > 0
          ? (user.non_billable_hours / maxUser.non_billable_hours) * 100
          : 0
      case 'late_entries':
        return maxUser.late_entries > 0
          ? (user.late_entries / maxUser.late_entries) * 100
          : 0
      case 'total_hours':
        return maxUser.total_hours > 0
          ? (user.total_hours / maxUser.total_hours) * 100
          : 0
      default:
        return 0
    }
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-md overflow-hidden mb-6">
      <div className="px-4 py-3 border-b border-slate-200 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
        <h2 className="text-sm font-bold text-slate-900">Top-N analysis</h2>
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-xs text-slate-600">Top</label>
          <select
            value={topN}
            onChange={(e) => setTopN(parseInt(e.target.value, 10))}
            className="text-xs border border-slate-200 rounded-lg px-2 py-1"
          >
            {[5, 10, 15, 20].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <select
            value={metric}
            onChange={(e) => setMetric(e.target.value as Metric)}
            className="text-xs border border-slate-200 rounded-lg px-2 py-1 min-w-[10rem]"
          >
            {METRICS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-8 text-slate-500 text-sm gap-2">
            <span className="inline-block h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            Loading rankings…
          </div>
        ) : getTopN.length === 0 ? (
          <p className="text-center text-sm text-slate-500 py-6">
            No data for the selected criteria
          </p>
        ) : (
          <ul className="space-y-1.5">
            {getTopN.map((user, index) => (
              <li key={user.user_id}>
                <button
                  type="button"
                  onClick={() => onUserSelect(user.user_id)}
                  className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 text-left"
                >
                  <span
                    className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center font-bold text-xs text-white ${
                      index === 0
                        ? 'bg-amber-500'
                        : index === 1
                          ? 'bg-slate-400'
                          : index === 2
                            ? 'bg-orange-600'
                            : 'bg-slate-500'
                    }`}
                  >
                    {index + 1}
                  </span>
                  <div className="flex-shrink-0 w-36 min-w-0">
                    <div className="font-medium text-xs truncate text-slate-900">
                      {user.full_name || user.username || 'Unknown'}
                    </div>
                    <div className="text-[10px] text-slate-500 truncate">
                      {user.username || '—'}
                    </div>
                  </div>
                  <div className="flex-grow min-w-0 h-4 rounded-full bg-slate-200 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all"
                      style={{ width: `${Math.max(barWidth(user), 2)}%` }}
                    />
                  </div>
                  <span className="flex-shrink-0 w-20 text-right text-xs font-semibold text-slate-900 tabular-nums">
                    {displayValue(user)}
                  </span>
                  <span
                    className={`flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                      user.is_active === 1 || user.is_active === true
                        ? 'bg-green-100 text-green-800'
                        : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    {user.is_active === 1 || user.is_active === true
                      ? 'Active'
                      : 'Inactive'}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {!loading && getTopN.length > 0 && (
        <div className="px-4 py-2 border-t border-slate-100 bg-slate-50 text-[10px] text-slate-500">
          Top {getTopN.length} of {topNData.length} · Click row for details
        </div>
      )}
    </div>
  )
}
